import React, { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'

import { CHAT } from 'constants/socket'
import useParticipants, {
    getIdentity,
} from 'hooks/useParticipants/useParticipants'
import useVideoContext from 'hooks/useVideoContext/useVideoContext'
import { GESTURE } from 'services/webSockets/gestures/constants'
import {
    dispatchReceiveChatMessage,
    dispatchSendSocketMessage,
    dispatchAddMessage,
} from 'store/dispatcher'
import { _websocketChatMsg } from 'store/selectors/WebSocket'

import ChatModal from './ChatModal/ChatModal'
import ChatToggleButton from './ChatToggleButton'

const getGestureText = gesture => {
    switch (gesture) {
        case 'party':
            return '🎉'
        case 'thumbsUp':
            return '👍'
        case 'confused':
            return '🤔'
        default:
            return '🙋'
    }
}

const ChatMobile = ({ shouldShowChat, setShouldShowChat }) => {
    const {
        room,
        roomName,
        userId,
        isGhost: shouldHideTextInput,
    } = useVideoContext()
    const localParticipant = room.localParticipant
    const localParticipantName = getIdentity(localParticipant?.identity)
        ?.fullName
    const [badgeCount, setBadgeCount] = useState(0)
    const [showChat, setShowChat] = useState(false)
    const chatMsg = useSelector(_websocketChatMsg)
    const { participants } = useParticipants()

    useEffect(() => {
        if (!chatMsg) return
        if (chatMsg.action === CHAT) onReceiveMsg(chatMsg)
        if (chatMsg.action === GESTURE) onReceiveGestureMsg(chatMsg)
        return () => dispatchReceiveChatMessage(null)
    }, [userId, chatMsg])

    useEffect(() => {
        if (shouldShowChat) {
            handleShowChat()
            setShouldShowChat(false)
        }
    }, [shouldShowChat])

    const handleShowChat = () => {
        setShowChat(true)
        setBadgeCount(0)
    }

    const handleHideChat = () => {
        setShowChat(false)
        setBadgeCount(0)
    }

    const onSendMsg = msg => {
        dispatchSendSocketMessage({
            action: CHAT,
            room: roomName,
            message: msg,
            name: localParticipantName,
            user_id: userId,
        })
    }

    const onReceiveMsg = msg => {
        if (!msg) return
        setBadgeCount(badgeCount + 1)

        const message = {
            senderUid: msg.user_id,
            senderName: msg.name,
            isSelf: msg.user_id === userId,
            text: msg.message,
            time: msg.timestamp,
            type: 'text',
        }
        dispatchAddMessage(message)
    }

    const getSenderName = senderUid => {
        const allParticipantsIdentity = [...participants, localParticipant].map(
            participant => participant.identity
        )
        const senderIdentity = allParticipantsIdentity.find(participantId =>
            participantId.startsWith(senderUid)
        )
        return senderIdentity
            ? getIdentity(senderIdentity).fullName
            : 'John Doe'
    }

    const onReceiveGestureMsg = msg => {
        if (!msg) return
        setBadgeCount(badgeCount + 1)
        const message = {
            senderUid: msg.data.from_user,
            senderName: getSenderName(msg.data.from_user),
            isSelf: msg.data.from_user === userId,
            text: getGestureText(msg.data.gesture),
            time: msg.timestamp,
            type: 'gesture',
        }
        dispatchAddMessage(message)
    }

    return (
        <>
            <ChatToggleButton
                handleShowChat={handleShowChat}
                handleHideChat={handleHideChat}
                badgeCount={badgeCount}
                showChat={showChat}
            />
            <ChatModal
                showChat={showChat}
                handleHideChat={handleHideChat}
                onSendMsg={onSendMsg}
                shouldShowTextInput={!shouldHideTextInput}
            />
        </>
    )
}

export default ChatMobile
